/**
 * antd → Mantine compatibility layer.
 *
 * O projeto não tem antd instalado. Esse arquivo expõe o namespace antd
 * (Form, Row, Col, Button, Checkbox, Select, etc) mas cada componente é
 * implementado como wrapper em torno de Mantine v9 — zero CSS/JS antd
 * carregado em runtime, comportamento e estilo são 100% Mantine.
 *
 * Mantemos o alias `"antd": "./src/shims/antd.js"` no vite.config.ts porque
 * SmartMap, SmartForm e GoToCoordinatesTool (legado React 16, ~4200 linhas)
 * importam o namespace antd. Migrar in-place esses arquivos é wave separada
 * — esse wrapper torna desnecessário fazê-lo só para tirar o shim.
 *
 * Forma original do antd v3/v4 (Form.create HOC, getFieldDecorator, etc) é
 * preservada o suficiente para os consumers atuais.
 */

import React, { Children, cloneElement, createElement, forwardRef } from 'react';
import {
  ActionIcon,
  Badge as MBadge,
  Box,
  Button as MButton,
  Card as MCard,
  Checkbox as MCheckbox,
  Divider as MDivider,
  Group,
  Grid,
  Input as MInput,
  LoadingOverlay,
  Modal as MModal,
  NumberInput,
  Pagination as MPagination,
  Popover as MPopover,
  Progress as MProgress,
  Radio as MRadio,
  Select as MSelect,
  SimpleGrid,
  Stack,
  Switch as MSwitch,
  Table as MTable,
  Tabs as MTabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications as mantineNotifications } from '@mantine/notifications';

// ─── Layout ───────────────────────────────────────────────────────────────────

// Antd Row → Mantine Grid (12-col)
export const Row = ({ children, gutter, className, style, type, align, justify }) => (
  <Grid
    gutter={Array.isArray(gutter) ? gutter[0] : gutter}
    align={align}
    justify={justify}
    className={className}
    style={{ position: 'relative', ...style }}
  >
    {children}
  </Grid>
);

// Antd Col span={N} (24-col grid) → Mantine Grid.Col span={N/2} (12-col grid)
export const Col = ({ children, span, xs, sm, md, lg, xl, offset, className, style, type }) => {
  // Antd 24-col → Mantine 12-col conversion (best effort)
  const conv = (n) => (n == null ? undefined : Math.max(1, Math.round((n / 24) * 12)));
  const spanProp = xs != null || sm != null || md != null || lg != null || xl != null
    ? { base: conv(xs ?? span ?? 24), sm: conv(sm), md: conv(md), lg: conv(lg), xl: conv(xl) }
    : (span != null ? conv(span) : 'auto');
  const offsetProp = offset != null
    ? (typeof offset === 'object' ? Object.fromEntries(Object.entries(offset).map(([k, v]) => [k, conv(v)])) : conv(offset))
    : undefined;
  return (
    <Grid.Col span={spanProp} offset={offsetProp} className={className} style={style}>
      {children}
    </Grid.Col>
  );
};

// ─── Buttons & feedback ───────────────────────────────────────────────────────

export const Button = forwardRef(({ children, onClick, disabled, className, type, size, loading, style, title, block, htmlType, shape }, ref) => {
  // Antd: type='primary'|'default'|'link'|'ghost'; size='small'|'middle'|'large'
  const variant = type === 'primary' ? 'filled'
    : type === 'link' ? 'subtle'
    : type === 'ghost' ? 'outline'
    : 'default';
  const msize = size === 'small' ? 'xs' : size === 'large' ? 'md' : 'sm';
  // shape='circle' → ActionIcon
  if (shape === 'circle') {
    return (
      <Tooltip label={title} disabled={!title}>
        <ActionIcon ref={ref} variant={variant === 'filled' ? 'filled' : 'subtle'} size={msize} onClick={onClick} disabled={disabled || loading} className={className} style={style}>
          {children}
        </ActionIcon>
      </Tooltip>
    );
  }
  return (
    <MButton
      ref={ref}
      type={htmlType || 'button'}
      variant={variant}
      size={msize}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      fullWidth={block}
      className={className}
      style={style}
      title={title}
    >
      {children}
    </MButton>
  );
});

export const Progress = ({ percent, status, size, showInfo }) => (
  <MProgress
    value={percent ?? 0}
    color={status === 'exception' ? 'red' : status === 'success' ? 'green' : 'blue'}
    size={size === 'small' ? 'sm' : 'md'}
  />
);

// ─── Checkbox / Switch / Radio ────────────────────────────────────────────────

// Antd onChange(e.target.checked|value) ≠ Mantine onChange(event). Wrappers
// detectam o consumer pattern; aceitamos ambos.
const _toggleChange = (cb) => (event) => {
  if (typeof cb === 'function') {
    const v = event && event.currentTarget ? event.currentTarget.checked : event;
    cb(v, event);
  }
};

export const Checkbox = ({ children, checked, onChange, disabled, value }) => (
  <MCheckbox
    label={children}
    checked={!!checked}
    onChange={_toggleChange(onChange)}
    disabled={disabled}
    value={value}
  />
);

export const Switch = ({ checked, onChange, disabled, size }) => (
  <MSwitch
    checked={!!checked}
    onChange={_toggleChange(onChange)}
    disabled={disabled}
    size={size === 'small' ? 'sm' : 'md'}
  />
);

export const Radio = ({ children, value, onChange, checked, disabled }) => (
  <MRadio
    label={children}
    value={value}
    onChange={(e) => onChange && onChange(e)}
    checked={!!checked}
    disabled={disabled}
  />
);
Radio.Group = ({ children, value, onChange, disabled, options }) => {
  // Antd <Radio.Group options={[{value, label}]} /> ou children
  const handleChange = (v) => onChange && onChange({ target: { value: v } });
  if (options) {
    return (
      <MRadio.Group value={value} onChange={handleChange}>
        <Stack gap="xs">
          {options.map((opt) => (
            <MRadio key={opt.value} value={opt.value} label={opt.label} disabled={disabled} />
          ))}
        </Stack>
      </MRadio.Group>
    );
  }
  return (
    <MRadio.Group value={value} onChange={handleChange}>
      <Stack gap="xs">{children}</Stack>
    </MRadio.Group>
  );
};

// ─── Select ───────────────────────────────────────────────────────────────────

const _selectDataFromChildren = (children) => {
  const opts = Children.toArray(children).filter(Boolean);
  return opts.map((opt) => ({
    value: String(opt.props?.value ?? ''),
    label: opt.props?.children ?? String(opt.props?.value ?? ''),
  }));
};

export const Select = ({ children, value, onChange, placeholder, disabled, style, allowClear, mode, size, showSearch }) => {
  const data = _selectDataFromChildren(children);
  return (
    <MSelect
      data={data}
      value={value == null ? null : String(value)}
      onChange={(v) => onChange && onChange(v)}
      placeholder={placeholder}
      disabled={disabled}
      style={style}
      clearable={!!allowClear}
      searchable={!!showSearch}
      size={size === 'small' ? 'xs' : size === 'large' ? 'md' : 'sm'}
    />
  );
};
Select.Option = ({ children, value }) => createElement('option', { value }, children);

// ─── Popover ──────────────────────────────────────────────────────────────────

export const Popover = ({ children, content, visible, onVisibleChange, title, placement, trigger, getPopupContainer }) => (
  <MPopover
    opened={!!visible}
    onChange={(opened) => onVisibleChange && onVisibleChange(opened)}
    position={placement === 'bottomLeft' ? 'bottom-start'
      : placement === 'bottomRight' ? 'bottom-end'
      : placement === 'rightTop' ? 'right-start'
      : placement === 'right' ? 'right'
      : placement === 'left' ? 'left'
      : placement === 'bottom' ? 'bottom'
      : 'top'}
    withArrow
  >
    <MPopover.Target>
      {React.isValidElement(children) ? children : <span>{children}</span>}
    </MPopover.Target>
    <MPopover.Dropdown>
      {title && <Title order={5} mb="xs">{title}</Title>}
      {content}
    </MPopover.Dropdown>
  </MPopover>
);

// ─── Notification ─────────────────────────────────────────────────────────────

export const notification = {
  warning: ({ message, description }) => mantineNotifications.show({ title: message, message: description, color: 'yellow' }),
  error:   ({ message, description }) => mantineNotifications.show({ title: message, message: description, color: 'red' }),
  success: ({ message, description }) => mantineNotifications.show({ title: message, message: description, color: 'green' }),
  info:    ({ message, description }) => mantineNotifications.show({ title: message, message: description, color: 'blue' }),
};

export const message = {
  success: (msg) => mantineNotifications.show({ message: typeof msg === 'string' ? msg : '', color: 'green' }),
  error:   (msg) => mantineNotifications.show({ message: typeof msg === 'string' ? msg : '', color: 'red' }),
  warning: (msg) => mantineNotifications.show({ message: typeof msg === 'string' ? msg : '', color: 'yellow' }),
  info:    (msg) => mantineNotifications.show({ message: typeof msg === 'string' ? msg : '', color: 'blue' }),
  loading: (msg) => mantineNotifications.show({ message: typeof msg === 'string' ? msg : '', loading: true }),
};

// ─── Input ────────────────────────────────────────────────────────────────────

const InputComponent = forwardRef(({ value, onChange, placeholder, disabled, maxLength, autoFocus, style, prefix, suffix, type, onPressEnter, ...rest }, ref) => (
  <TextInput
    ref={ref}
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    maxLength={maxLength}
    autoFocus={autoFocus}
    style={style}
    leftSection={prefix}
    rightSection={suffix}
    type={type}
    onKeyDown={(e) => { if (e.key === 'Enter' && onPressEnter) onPressEnter(e); }}
    {...rest}
  />
));

InputComponent.Search = ({ value, onChange, onSearch, placeholder, disabled, maxLength, autoFocus, enterButton }) => (
  <TextInput
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    maxLength={maxLength}
    autoFocus={autoFocus}
    onKeyDown={(e) => { if (e.key === 'Enter' && onSearch) onSearch(value); }}
    rightSectionWidth={enterButton ? 92 : undefined}
    rightSection={enterButton
      ? <MButton size="xs" onClick={() => onSearch && onSearch(value)}>{enterButton === true ? 'Buscar' : enterButton}</MButton>
      : undefined}
  />
);

InputComponent.TextArea = ({ value, onChange, rows, placeholder, disabled, style, maxLength }) => (
  <Textarea
    value={value ?? ''}
    onChange={onChange}
    minRows={rows}
    autosize
    placeholder={placeholder}
    disabled={disabled}
    style={style}
    maxLength={maxLength}
  />
);

export const Input = InputComponent;

export const InputNumber = ({ value, onChange, placeholder, style, step, precision, min, max, disabled }) => (
  <NumberInput
    value={value ?? ''}
    onChange={(v) => onChange && onChange(typeof v === 'number' ? v : parseFloat(v))}
    placeholder={placeholder}
    style={style}
    step={step}
    decimalScale={precision}
    min={min}
    max={max}
    disabled={disabled}
  />
);

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card = ({ children, style, className, title, extra, bordered, type }) => (
  <MCard withBorder={bordered !== false} shadow="sm" padding="md" className={className} style={style}>
    {(title || extra) && (
      <MCard.Section withBorder inheritPadding py="xs">
        <Group justify="space-between">
          {title && <Text fw={600}>{title}</Text>}
          {extra}
        </Group>
      </MCard.Section>
    )}
    {children}
  </MCard>
);

// ─── Table ────────────────────────────────────────────────────────────────────

// API antd: columns=[{title, dataIndex, key, render, width, className}], dataSource
export const Table = ({ columns, dataSource, pagination, rowKey, size, onChange, loading, className, showHeader, expandedRowRender, expandedRowKeys, onExpandedRowsChange }) => {
  if (!dataSource) return null;
  const data = Array.isArray(dataSource) ? dataSource : [];
  const cols = columns || [];

  return (
    <Box pos="relative">
      {loading && <LoadingOverlay visible zIndex={5} overlayProps={{ blur: 1, backgroundOpacity: 0.5 }} />}
      <MTable className={className} striped highlightOnHover withTableBorder>
        {showHeader !== false && (
          <MTable.Thead>
            <MTable.Tr>
              {cols.map((col, i) => (
                <MTable.Th key={col.key ?? col.dataIndex ?? i} style={{ width: col.width }}>
                  {col.title}
                </MTable.Th>
              ))}
            </MTable.Tr>
          </MTable.Thead>
        )}
        <MTable.Tbody>
          {data.map((item, i) => {
            const key = typeof rowKey === 'function' ? rowKey(item) : (item?.[rowKey] ?? i);
            const isExpanded = expandedRowKeys && expandedRowKeys.includes(key);
            return (
              <React.Fragment key={key}>
                <MTable.Tr>
                  {cols.map((col, ci) => (
                    <MTable.Td key={col.key ?? col.dataIndex ?? ci} className={col.className}>
                      {col.render
                        ? col.render(col.dataIndex ? item?.[col.dataIndex] : item, item, i)
                        : col.dataIndex
                        ? item?.[col.dataIndex]
                        : null}
                    </MTable.Td>
                  ))}
                </MTable.Tr>
                {expandedRowRender && isExpanded && (
                  <MTable.Tr>
                    <MTable.Td colSpan={cols.length}>{expandedRowRender(item)}</MTable.Td>
                  </MTable.Tr>
                )}
              </React.Fragment>
            );
          })}
        </MTable.Tbody>
      </MTable>
      {pagination && pagination.total > pagination.pageSize && (
        <Group justify="center" mt="md">
          <MPagination
            total={Math.ceil(pagination.total / pagination.pageSize)}
            value={pagination.current ?? 1}
            onChange={(p) => onChange && onChange({ current: p, pageSize: pagination.pageSize })}
          />
        </Group>
      )}
    </Box>
  );
};

// ─── Tag ──────────────────────────────────────────────────────────────────────

export const Tag = ({ children, color, status, className }) => (
  <MBadge
    color={status === 'error' ? 'red'
      : status === 'success' ? 'green'
      : status === 'warning' ? 'yellow'
      : color === 'blue' ? 'blue'
      : color === 'red' ? 'red'
      : color === 'green' ? 'green'
      : 'gray'}
    variant="light"
    className={className}
  >
    {children}
  </MBadge>
);

// ─── Badge ────────────────────────────────────────────────────────────────────

export const Badge = ({ children, status, count, className, style }) => {
  if (count != null) {
    return (
      <Box style={{ position: 'relative', display: 'inline-block', ...style }} className={className}>
        {children}
        <MBadge size="xs" style={{ position: 'absolute', top: -4, right: -4 }}>{count}</MBadge>
      </Box>
    );
  }
  return (
    <Box className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}>
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: status === 'default' ? '#d9d9d9'
            : status === 'success' ? '#52c41a'
            : status === 'error' ? '#ff4d4f'
            : status === 'warning' ? '#faad14'
            : '#1677ff',
        }}
      />
      {children}
    </Box>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────

export const Modal = ({ children, visible, opened, onCancel, onClose, title, footer, width, wrapClassName, maskClosable, icon, bodyStyle, style }) => (
  <MModal
    opened={!!(visible ?? opened)}
    onClose={onCancel || onClose || (() => {})}
    title={title}
    size={width ? (typeof width === 'number' ? `${width}px` : width) : 'md'}
    closeOnClickOutside={maskClosable !== false}
    classNames={{ root: wrapClassName }}
    padding="md"
    styles={{ body: bodyStyle }}
    style={style}
  >
    {children}
    {footer && <Box mt="md" style={{ textAlign: 'right' }}>{footer}</Box>}
  </MModal>
);

Modal.confirm = ({ title, content, okText = 'OK', cancelText = 'Cancelar', onOk, onCancel }) => {
  if (window.confirm(typeof title === 'string' ? title : (typeof content === 'string' ? content : 'Confirmar?'))) {
    onOk && onOk();
  } else {
    onCancel && onCancel();
  }
};
Modal.success = ({ title }) => mantineNotifications.show({ message: typeof title === 'string' ? title : 'Sucesso', color: 'green' });
Modal.error   = ({ title }) => mantineNotifications.show({ message: typeof title === 'string' ? title : 'Erro', color: 'red' });
Modal.warning = ({ title }) => mantineNotifications.show({ message: typeof title === 'string' ? title : 'Atenção', color: 'yellow' });
Modal.info    = ({ title }) => mantineNotifications.show({ message: typeof title === 'string' ? title : '', color: 'blue' });

// ─── Spin ─────────────────────────────────────────────────────────────────────

export const Spin = ({ children, spinning, tip, size, style }) => (
  <Box pos="relative" style={style}>
    <LoadingOverlay
      visible={!!spinning}
      zIndex={10}
      loaderProps={{ size: size === 'small' ? 'sm' : size === 'large' ? 'lg' : 'md' }}
      overlayProps={{ blur: 1, backgroundOpacity: 0.6 }}
    />
    {children}
  </Box>
);

// ─── Form (HOC pattern do antd v3/v4) ─────────────────────────────────────────

// Mantém a API antiga: const WrappedForm = Form.create()(Component); Component
// recebe props.form com { getFieldDecorator, validateFields, setFieldsValue,
// getFieldValue, resetFields }. Estado dos campos vive num plain object;
// componentes filhos viram controlled via cloneElement.
export const Form = ({ children, onSubmit, layout, style, className }) => (
  <form onSubmit={onSubmit} style={style} className={className}>
    <Stack gap="md">{children}</Stack>
  </form>
);

Form.Item = ({ children, label, required, help, validateStatus, labelCol, wrapperCol, style, className }) => (
  <Box className={className} style={style}>
    {label && (
      <Text fw={500} size="sm" mb={4}>
        {label}{required && <Text component="span" c="red"> *</Text>}
      </Text>
    )}
    {children}
    {help && <Text size="xs" c={validateStatus === 'error' ? 'red' : 'dimmed'} mt={4}>{help}</Text>}
  </Box>
);

Form.create = () => (WrappedComponent) => {
  // Stateful HOC — cada instância tem seu próprio fields state.
  return class FormHOC extends React.Component {
    constructor(props) {
      super(props);
      this.state = { fields: {} };
      this.form = {
        getFieldDecorator: (name, opts = {}) => (element) => {
          const initialValue = this.state.fields[name] ?? opts.initialValue ?? '';
          return cloneElement(element, {
            value: initialValue,
            onChange: (e) => {
              const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
              this.setState((s) => ({ fields: { ...s.fields, [name]: v } }));
            },
          });
        },
        getFieldValue: (name) => this.state.fields[name],
        setFieldsValue: (values) => this.setState((s) => ({ fields: { ...s.fields, ...values } })),
        validateFields: (cb) => cb(null, this.state.fields),
        resetFields: () => this.setState({ fields: {} }),
      };
    }
    render() {
      return React.createElement(WrappedComponent, { ...this.props, form: this.form });
    }
  };
};

// ─── Divider ──────────────────────────────────────────────────────────────────

export const Divider = ({ children, orientation, style, type }) => (
  <MDivider
    label={children}
    labelPosition={orientation === 'left' ? 'left' : orientation === 'right' ? 'right' : 'center'}
    orientation={type === 'vertical' ? 'vertical' : 'horizontal'}
    my="md"
    style={style}
  />
);

// ─── DatePicker ───────────────────────────────────────────────────────────────

export const DatePicker = ({ value, onChange, placeholder, disabled, format, style, allowClear }) => (
  <DateInput
    value={value || null}
    onChange={(v) => onChange && onChange(v)}
    placeholder={placeholder}
    disabled={disabled}
    valueFormat={format || 'DD/MM/YYYY'}
    clearable={allowClear !== false}
    style={style}
  />
);

// ─── Upload ───────────────────────────────────────────────────────────────────

export const Upload = ({ children, onChange, accept, disabled, beforeUpload, multiple }) => (
  <Box>
    <input
      type="file"
      accept={accept}
      disabled={disabled}
      multiple={multiple}
      onChange={(e) => {
        const files = Array.from(e.target.files || []);
        if (beforeUpload && files[0]) {
          const res = beforeUpload(files[0], files);
          if (res === false) return;
        }
        onChange && onChange({ file: files[0], fileList: files });
      }}
    />
    {children}
  </Box>
);

Upload.Dragger = ({ children, onChange, accept, disabled, multiple }) => (
  <Box
    style={{
      border: '2px dashed var(--mantine-color-gray-4)',
      borderRadius: 4,
      padding: 24,
      textAlign: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    <input
      type="file"
      accept={accept}
      disabled={disabled}
      multiple={multiple}
      style={{ display: 'none' }}
      onChange={(e) => {
        const files = Array.from(e.target.files || []);
        onChange && onChange({ file: files[0], fileList: files });
      }}
    />
    {children}
  </Box>
);

// ─── Tabs (antd v4 API com Tabs.TabPane) ──────────────────────────────────────

// Children.toArray rewrites keys ("15464-70" -> ".$15464-70"), so they would
// never match defaultActiveKey/activeKey. Children.forEach yields the original
// elements with the keys the caller wrote.
const _tabPanes = (children) => {
  const panes = [];
  Children.forEach(children, (child) => {
    if (child) panes.push({ key: String(child.key ?? panes.length), element: child });
  });
  return panes;
};

class TabsComponent extends React.Component {
  constructor(props) {
    super(props);
    const initial = props.defaultActiveKey ?? props.activeKey ?? _tabPanes(props.children)[0]?.key;
    this.state = { activeKey: initial != null ? String(initial) : null };
  }
  componentDidUpdate(prev) {
    if (prev.activeKey !== this.props.activeKey && this.props.activeKey != null) {
      this.setState({ activeKey: String(this.props.activeKey) });
    }
  }
  render() {
    const { children, onChange, style, className, tabPosition } = this.props;
    const { activeKey } = this.state;
    const panes = _tabPanes(children);
    const setKey = (key) => {
      this.setState({ activeKey: key });
      if (onChange) onChange(key);
    };
    return (
      <MTabs value={activeKey ?? ''} onChange={(v) => setKey(v)} orientation={tabPosition === 'left' ? 'vertical' : 'horizontal'} className={className} style={style}>
        <MTabs.List>
          {panes.map((p) => (
            <MTabs.Tab key={p.key} value={p.key}>
              {p.element.props.tab}
            </MTabs.Tab>
          ))}
        </MTabs.List>
        {panes.map((p) => (
          <MTabs.Panel key={p.key} value={p.key} pt="md">
            {p.element.props.children}
          </MTabs.Panel>
        ))}
      </MTabs>
    );
  }
}
TabsComponent.TabPane = ({ children }) => children;
export const Tabs = TabsComponent;

// ─── List ─────────────────────────────────────────────────────────────────────

export const List = ({ children, dataSource, renderItem, itemLayout, className, style, header, footer, bordered }) => {
  const items = renderItem && dataSource ? dataSource.map((item, i) => renderItem(item, i)) : children;
  return (
    <Box className={className} style={{ ...(bordered ? { border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 } : {}), ...style }}>
      {header && <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>{header}</Box>}
      <Stack gap={0}>{items}</Stack>
      {footer && <Box p="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>{footer}</Box>}
    </Box>
  );
};
List.Item = ({ children, actions, extra, className, style }) => (
  <Box className={className} style={{ padding: '8px 12px', borderBottom: '1px solid var(--mantine-color-gray-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...style }}>
    <Box style={{ flex: 1 }}>{children}</Box>
    {actions && <Group gap="xs">{actions}</Group>}
    {extra && <Box ml="md">{extra}</Box>}
  </Box>
);
List.Item.Meta = ({ title, description, avatar }) => (
  <Group gap="sm" wrap="nowrap">
    {avatar}
    <Box>
      {title && <Text fw={600}>{title}</Text>}
      {description && <Text size="sm" c="dimmed">{description}</Text>}
    </Box>
  </Group>
);

// ─── Pagination ───────────────────────────────────────────────────────────────

export const Pagination = ({ total, pageSize, current, onChange, style, showSizeChanger }) => (
  <Box style={{ textAlign: 'center', ...style }}>
    <MPagination
      total={Math.max(1, Math.ceil((total || 0) / (pageSize || 10)))}
      value={current ?? 1}
      onChange={(p) => onChange && onChange(p, pageSize)}
    />
  </Box>
);

// ─── Typography ───────────────────────────────────────────────────────────────

export const Typography = {
  Text: ({ children, strong, type, className, style }) => (
    <Text
      component={strong ? 'strong' : 'span'}
      fw={strong ? 600 : undefined}
      c={type === 'secondary' ? 'dimmed' : type === 'danger' ? 'red' : type === 'warning' ? 'yellow' : type === 'success' ? 'green' : undefined}
      className={className}
      style={style}
    >
      {children}
    </Text>
  ),
  Title: ({ children, level = 1, className, style }) => (
    <Title order={level} className={className} style={style}>{children}</Title>
  ),
  Paragraph: ({ children, className, style }) => (
    <Text className={className} style={style}>{children}</Text>
  ),
};
